/** @file The second point B of a two-point `parent` part, entity-relative (design 21.5, 21.7). */
import type { Anchor } from '@zilo/pose3d-format/types/drawable';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { rotateZScaled } from '@zilo/pose3d-pose/chain/rotateZScaled';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { RENDER_CORE_ERRORS } from '@zilo/pose3d-render-core/errors/renderCoreErrors';
import type { NodePoints } from '@zilo/pose3d-render-core/place/nodePointsOf';
import { poseNodeOf } from '@zilo/pose3d-render-core/place/poseNodeOf';
import { snapRelOf } from '@zilo/pose3d-render-core/place/snapRelOf';

/** What the anchor names: another node's rounded position, a point along the node's own bone, else the bone origin. */
function targetOf(pose: PoseBuffer, points: NodePoints, anchor: Anchor | undefined, nodeIndices: Map<string, number>, view: View, zScaleRotate: number): Vec3 | undefined {
  if (anchor?.node !== undefined) {
    const i = nodeIndices.get(anchor.node) ?? throwError(RENDER_CORE_ERRORS.NODE_UNKNOWN, { node: anchor.node });
    return snapRelOf(poseNodeOf(pose, i).pos, view);
  }
  if (anchor?.offset !== undefined) {
    const s = points.scale;
    const r = rotateZScaled(points.q, [anchor.offset[0] * s, anchor.offset[1] * s, anchor.offset[2] * s], zScaleRotate);
    const parts = points.parts;
    if (parts === undefined) return [points.o[0] + r[0], points.o[1] + r[1], points.o[2] + r[2]];
    const o = parts.map.back(points.o, parts.name);
    return parts.map.move([o[0] + r[0], o[1] + r[1], o[2] + r[2]]);
  }
  return points.root ? undefined : points.o;
}

/**
 * `B = A + (P - A) factor` (`anchor.factor ?? 1`, may be < 1 or negative) with P = `anchor.node`'s rounded position
 * (`NODE_UNKNOWN` if absent), else `O + Zs(q, s anchor.offset)` (not rounded, 21.7; with the host part map built from
 * `back(O)` and moved once, 22.3), else the rounded bone origin O;
 * `undefined` for a root with neither (no second point). `pixelOffset` never moves B (21.5: it is the image inset).
 */
export function anchorPointOf(pose: PoseBuffer, points: NodePoints, anchor: Anchor | undefined, nodeIndices: Map<string, number>, view: View, zScaleRotate: number): Vec3 | undefined {
  const p = targetOf(pose, points, anchor, nodeIndices, view, zScaleRotate);
  if (p === undefined) return undefined;
  const f = anchor?.factor ?? 1;
  const a = points.a;
  return [a[0] + (p[0] - a[0]) * f, a[1] + (p[1] - a[1]) * f, a[2] + (p[2] - a[2]) * f];
}
